import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  subscriptionService,
  TipoPlanoEnum,
  AssinaturaExpiracaoResponseDto,
  AssinaturaResponseDto,
} from './service/subscription.service';

type Period = 'monthly' | 'annual';

type PlanPrice = {
  price: string;
  per: string;
  tipoPlano: TipoPlanoEnum;
};

type SubscriptionPlan = {
  name: string;
  isPopular: boolean;
  features: string[];
  prices: Record<Period, PlanPrice>;
};

// --- Dados Mockados ---
const plans: SubscriptionPlan[] = [
  {
    name: 'Básico',
    isPopular: false,
    features: [
      'Análise de até 5 empresas',
      'Alertas via Telegram',
      'Relatórios via email',
      'Dashboard com visão geral',
      'Suporte por email',
    ],
    prices: {
      monthly: { price: '19,90', per: 'mês', tipoPlano: 'BASIC_MENSAL' },
      annual: { price: '199,90', per: 'ano', tipoPlano: 'BASIC_ANUAL' },
    },
  },
  {
    name: 'Premium',
    isPopular: true,
    features: [
      'Análise de até 50 empresas',
      'Alertas via Telegram',
      'Relatórios via email',
      'Dashboard completo com insights',
      'Suporte por email',
    ],
    prices: {
      monthly: { price: '29,90', per: 'mês', tipoPlano: 'PLUS_MENSAL' },
      annual: { price: '299,90', per: 'ano', tipoPlano: 'PLUS_ANUAL' },
    },
  },
  {
    name: 'Pro',
    isPopular: false,
    features: [
      'Análise de empresas ilimitadas',
      'Alertas via Telegram',
      'Relatórios via email',
      'Dashboard Pro com IA',
      'Suporte dedicado 24/7',
      'Acesso antecipado a novas features',
    ],
    prices: {
      monthly: { price: '49,90', per: 'mês', tipoPlano: 'PRO_MENSAL' },
      annual: { price: '499,90', per: 'ano', tipoPlano: 'PRO_ANUAL' },
    },
  },
];

const SubscriptionPage: React.FC = () => {
  const priceIdByPlanType: Record<TipoPlanoEnum, string> = {
    BASIC_MENSAL: 'price_1T7bJ1PobpLvEIG4jKrrErsC',
    BASIC_ANUAL: 'price_1T7aRMPobpLvEIG4fXsAKEVX',
    PLUS_MENSAL: 'price_1T7bJVPobpLvEIG4eDbr7Hz6',
    PLUS_ANUAL: 'price_1T7bLMPobpLvEIG4AHkLhzst',
    PRO_MENSAL: 'price_1T7bK7PobpLvEIG4faD2l1TJ',
    PRO_ANUAL: 'price_1T7bKzPobpLvEIG4SAwkrM6o',
  };
  const [selectedPeriodsByPlan, setSelectedPeriodsByPlan] = useState<Record<string, Period>>(
    () =>
      plans.reduce((acc, plan) => {
        acc[plan.name] = 'annual';
        return acc;
      }, {} as Record<string, Period>)
  );
  const [loading, setLoading] = useState(true);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [hasAcceptedUpgradeTerms, setHasAcceptedUpgradeTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [subscriptionExpiration, setSubscriptionExpiration] = useState<AssinaturaExpiracaoResponseDto | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<AssinaturaResponseDto | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const isUpgradeFlow = new URLSearchParams(location.search).get('upgrade') === 'true';
  const termsText = `TERMOS E CONDIÇÕES DE ASSINATURA – SINALINVEST
Última atualização: Fevereiro de 2026

Ao assinar a plataforma SinalInvest, você ("USUÁRIO") concorda com os termos abaixo descritos. A utilização dos serviços implica na aceitação integral destas condições.

1. DO PERÍODO DE TESTE GRATUITO (TRIAL DE 7 DIAS)
1.1. Mecanismo: A SinalInvest oferece um período de teste gratuito de 7 (sete) dias corridos para novos assinantes. Para ativar o teste, o USUÁRIO deve cadastrar um meio de pagamento válido (Cartão de Crédito).

1.2. Cobrança: Nenhuma cobrança será realizada no momento do cadastro. O valor integral do plano escolhido será debitado automaticamente no cartão cadastrado apenas no 8º (oitavo) dia, caso o USUÁRIO não efetue o cancelamento antes desse prazo.

1.3. Cancelamento do Teste: O USUÁRIO pode cancelar a assinatura a qualquer momento dentro dos 7 dias de teste através do painel de controle ("Minha Conta"). Neste caso, nenhuma cobrança será efetuada e o acesso será interrompido ao final do 7º dia.

2. DOS PLANOS E PAGAMENTOS
2.1. Renovação Automática: Todos os planos (Mensal ou Anual) funcionam sob o modelo de assinatura recorrente. A renovação ocorre automaticamente ao final de cada ciclo, utilizando o mesmo meio de pagamento, salvo se houver cancelamento prévio.

2.2. Plano Mensal: Garante acesso à plataforma por 30 dias. A cobrança é realizada mensalmente.
2.3. Plano Anual: Garante acesso à plataforma por 12 meses. O valor total pode ser pago à vista ou parcelado, conforme condições comerciais vigentes. O desconto oferecido no plano anual é condicionado à permanência pelo período de 12 meses.

3. POLÍTICA DE CANCELAMENTO E REEMBOLSO
3.1. Após o Período de Teste (7 dias): Passado o período de teste gratuito e efetuada a primeira cobrança, o serviço é considerado contratado e entregue.

3.2. Cancelamento de Plano Mensal: O USUÁRIO pode cancelar a renovação a qualquer momento. O acesso permanecerá ativo até o fim do ciclo mensal já pago. Não haverá reembolso proporcional (pró-rata) dos dias não utilizados.

3.3. Cancelamento de Plano Anual: O cancelamento impede a renovação automática para o próximo ano. O USUÁRIO continuará com acesso até o término dos 12 meses contratados. Não haverá reembolso total ou parcial do valor pago, visto que o desconto concedido pressupõe a fidelidade contratual de 1 ano.

4. ISENÇÃO DE RESPONSABILIDADE FINANCEIRA (DISCLAIMER)
4.1. Natureza do Serviço: A SinalInvest é uma ferramenta tecnológica de análise de dados baseada em Inteligência Artificial. Não somos uma casa de análise (research), consultoria de valores mobiliários ou gestora de recursos.

4.2. Ausência de Recomendação: As classificações de impacto ("Positivo", "Negativo", "Neutro") geradas pela nossa IA referem-se estritamente à interpretação semântica da notícia e dados correlatos. Nenhuma informação na plataforma constitui recomendação de compra, venda ou manutenção de ativos financeiros.

4.3. Riscos de Mercado: O mercado financeiro é volátil e envolve riscos. O USUÁRIO reconhece que é o único responsável por suas decisões de investimento. A SinalInvest não se responsabiliza por quaisquer perdas financeiras, danos diretos ou indiretos decorrentes do uso das informações fornecidas pela plataforma.

4.4. Precisão da IA: Embora utilizemos tecnologia de ponta, modelos de Inteligência Artificial podem cometer erros de interpretação ("alucinações"). O USUÁRIO deve sempre verificar as fontes originais das notícias (https://www.google.com/search?q=links fornecidos) antes de tomar decisões críticas.

5. PROPRIEDADE INTELECTUAL E USO DE DADOS
5.1. É estritamente proibido copiar, redistribuir, vender ou realizar engenharia reversa ("scraping") dos dados, análises ou estrutura da plataforma SinalInvest sem autorização expressa.

5.2. O compartilhamento de login e senha é proibido. O sistema possui monitoramento de sessões simultâneas e poderá bloquear contas com atividade suspeita de compartilhamento.

6. DISPOSIÇÕES GERAIS
6.1. A SinalInvest reserva-se o direito de alterar os preços dos planos mediante aviso prévio de 30 dias via e-mail. O USUÁRIO terá o direito de cancelar a assinatura antes da vigência do novo valor caso não concorde com o reajuste.`;

  const refreshUserSession = async () => {
    try {
      await auth.signinSilent();
    } catch (error) {
      console.error('Erro ao atualizar sessão do usuário após mudança de plano:', error);
    }
  };

  const formatExpirationDate = (expirationData: AssinaturaExpiracaoResponseDto): string | null => {
    if (expirationData.expirationIso) {
      const date = new Date(expirationData.expirationIso);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
    }

    if (typeof expirationData.expiration === 'number') {
      const date = new Date(expirationData.expiration);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
    }

    return null;
  };

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        if (!isUpgradeFlow) {
          const portal = await subscriptionService.buscarPortalAssinatura();
          if (portal?.url) {
            setPortalUrl(portal.url);
            setSubscriptionExpiration(null);
            const sub = await subscriptionService.buscarAssinatura();
            setCurrentSubscription(sub);
            return;
          }
          setPortalUrl(null);
          setCurrentSubscription(null);
          const expirationData = await subscriptionService.buscarExpiracaoAssinatura();
          setSubscriptionExpiration(expirationData);
          return;
        }

        const sub = await subscriptionService.buscarAssinatura();
        if (sub) {
          setCurrentSubscription(sub);
          setSubscriptionExpiration(null);
        } else {
          setCurrentSubscription(null);
          const expirationData = await subscriptionService.buscarExpiracaoAssinatura();
          setSubscriptionExpiration(expirationData);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [navigate, isUpgradeFlow]);

  const handlePeriodSelect = (planName: string, period: Period) => {
    setSelectedPeriodsByPlan((prev) => ({
      ...prev,
      [planName]: period,
    }));
  };

  const handleSubscribeClick = async (planName: string, period: Period) => {
    const plan = plans.find(p => p.name === planName);
    if (!plan) return;

    const selectedPlanType = plan.prices[period].tipoPlano;

    if (!isUpgradeFlow) {
      const priceId = priceIdByPlanType[selectedPlanType];
      if (!priceId) {
        alert('Plano indisponível no momento. Tente novamente mais tarde.');
        return;
      }
      try {
        setIsUpdatingPlan(true);
        const checkout = await subscriptionService.criarCheckout({ priceId });
        if (!checkout?.url) {
          alert('Não foi possível iniciar o checkout. Tente novamente.');
          return;
        }
        window.location.assign(checkout.url);
      } catch (error) {
        console.error('Erro ao criar checkout de assinatura:', error);
        alert('Não foi possível iniciar o checkout. Tente novamente.');
      } finally {
        setIsUpdatingPlan(false);
      }
        return;
    }

    if (isUpgradeFlow) {
      if (!hasAcceptedUpgradeTerms) {
        alert('Você precisa aceitar os Termos de Assinatura para atualizar o plano.');
        return;
      }
      try {
        setIsUpdatingPlan(true);
        await subscriptionService.atualizarPlano({ novoPlano: selectedPlanType });
        await refreshUserSession();
        alert('Plano atualizado com sucesso!');
        navigate('/assinatura/gerenciar');
      } catch (error) {
        console.error('Erro ao atualizar plano:', error);
        alert('Não foi possível atualizar o plano. Tente novamente.');
      } finally {
        setIsUpdatingPlan(false);
      }
      return;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-8 md:px-6 md:py-12">
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Planos de Assinatura</h1>
          <p className="text-xl text-gray-600">Escolha o plano ideal para seus investimentos e mude a qualquer momento.</p>
        </div>

        {!isUpgradeFlow && portalUrl && (
          <div className="mb-8 rounded-2xl border border-blue-200 bg-blue-50 p-6 md:p-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Assinatura ativa</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900">
                  {currentSubscription?.plano || 'Plano ativo'}
                </h2>
                <p className="mt-2 text-sm text-gray-700">
                  Sua assinatura já está ativa. Use o portal para gerenciar cobrança, cartão e cancelamento.
                </p>
              </div>
              <button
                type="button"
                onClick={() => window.location.assign(portalUrl)}
                className="inline-flex items-center justify-center rounded-lg bg-blue-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
              >
                Gerenciar assinatura
              </button>
            </div>
          </div>
        )}

        {subscriptionExpiration && (
          <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900">
            <p className="text-sm md:text-base">
              Você tem acesso ao plano <strong>{subscriptionExpiration.plano || 'atual'}</strong>
              {formatExpirationDate(subscriptionExpiration)
                ? ` até ${formatExpirationDate(subscriptionExpiration)}.`
                : '.'}
            </p>
          </div>
        )}

        {!portalUrl && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {plans.map(plan => {
            const selectedPeriod = selectedPeriodsByPlan[plan.name] || 'annual';
            return (
              <div
                key={plan.name}
                className="flex flex-col bg-white rounded-2xl border border-gray-200 relative transition-all duration-300 overflow-hidden hover:-translate-y-1 hover:shadow-lg"
              >
                {plan.isPopular && (
                  <div className="absolute top-5 right-5 bg-blue-700 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm z-10">
                    Mais Popular
                  </div>
                )}
                
                <div className="flex flex-col flex-grow">
                  <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                    <div className="mb-6">
                      <div className="inline-flex w-full rounded-lg border border-gray-200 bg-gray-100 p-1">
                        <button
                          type="button"
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                            selectedPeriod === 'monthly'
                              ? 'bg-white text-blue-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                          onClick={() => handlePeriodSelect(plan.name, 'monthly')}
                        >
                          Mensal
                        </button>
                        <button
                          type="button"
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                            selectedPeriod === 'annual'
                              ? 'bg-white text-blue-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                          onClick={() => handlePeriodSelect(plan.name, 'annual')}
                        >
                          Anual
                        </button>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-sm text-gray-500">Planos pensados para cada perfil de investidor.</p>
                    <div className="mt-5">
                      <span className="text-3xl font-extrabold text-blue-700">R$ {plan.prices[selectedPeriod].price}</span>
                      <span className="text-sm text-gray-500 ml-2">/ {plan.prices[selectedPeriod].per}</span>
                    </div>
                  </div>

                  <div className="px-8 py-6 flex-grow">
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Benefícios</p>
                    <ul className="list-none p-0 flex-grow space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3 text-gray-900">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                    </ul>
                  </div>

                  <div className="px-8 pb-8">
                    {isUpgradeFlow && (
                      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <label className="flex items-start gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={hasAcceptedUpgradeTerms}
                            onChange={(e) => setHasAcceptedUpgradeTerms(e.target.checked)}
                            className="mt-0.5"
                          />
                          <span>
                            Li e concordo com os{' '}
                            <button
                              type="button"
                              className="text-blue-700 underline"
                              onClick={() => setIsTermsModalOpen(true)}
                            >
                              Termos de Assinatura
                            </button>
                            .
                          </span>
                        </label>
                      </div>
                    )}
                    <button
                      type="button"
                      className={`w-full p-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        isUpgradeFlow
                          ? 'bg-blue-700 text-white hover:bg-blue-800'
                          : 'border border-gray-200 text-gray-700 hover:border-blue-700 hover:text-blue-700'
                      } ${(isUpdatingPlan || (isUpgradeFlow && !hasAcceptedUpgradeTerms)) ? 'opacity-70 cursor-not-allowed' : ''}`}
                      onClick={() => handleSubscribeClick(plan.name, selectedPeriod)}
                      disabled={isUpdatingPlan || (isUpgradeFlow && !hasAcceptedUpgradeTerms)}
                    >
                      {isUpgradeFlow
                        ? isUpdatingPlan
                          ? 'Atualizando plano...'
                          : `Assinar ${plan.name}`
                        : 'Selecionar plano'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>
      
      {isTermsModalOpen && (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4" onClick={() => setIsTermsModalOpen(false)}>
          <div className="w-full max-w-4xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Termos de Assinatura</h3>
              <button
                type="button"
                className="text-2xl leading-none text-gray-500 hover:text-gray-700"
                onClick={() => setIsTermsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <pre className="m-0 p-5 overflow-y-auto whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
              {termsText}
            </pre>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionPage;